import type { AthleteWritePayload, SportAthlete } from './api'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { createSportAthlete, deleteSportAthlete, fetchSportAthletes, updateSportAthlete } from './api'

interface Props {
  sportId: number
  // Trading+ — same gate as the rest of the /trading page.
  canEdit: boolean
}

// Calendar-accurate age from a YYYY-MM-DD birthdate: year difference, minus
// one when this year's birthday hasn't happened yet. Parses the string
// directly rather than through Date to dodge timezone off-by-one-day shifts.
function computeAge(birthdate: string, now: Date = new Date()): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthdate)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  let age = now.getFullYear() - y
  const beforeBirthday = now.getMonth() + 1 < mo || (now.getMonth() + 1 === mo && now.getDate() < d)
  if (beforeBirthday) age -= 1
  return age
}

interface AthleteRow extends SportAthlete {
  age: number | null
}

interface Group {
  label: string
  rows: AthleteRow[]
  // UNDER 18 rows get the subtle warn accent — the trading process checks minors.
  minor?: boolean
  // UNKNOWN only renders when it has members.
  hideWhenEmpty?: boolean
  // Desktop 2-column assignment: 21+ (nearly the whole roster, ~4k for
  // WoO/HLR) alone on the left; UNDER 21 → UNDER 18 → UNKNOWN stacked
  // on the right.
  column: 'left' | 'right'
}

interface GroupListProps {
  groups: Group[]
  canEdit: boolean
  busy: boolean
  // Search results render as one full-width list instead of the
  // three-column age-bracket layout.
  single?: boolean
  // Both resolve true on success so the row can leave edit mode; the parent
  // owns the msg/busy/reload lifecycle (and the delete confirm).
  onSave: (athleteId: number, payload: AthleteWritePayload) => Promise<boolean>
  onDelete: (a: SportAthlete) => Promise<boolean>
  onToggleActive: (a: SportAthlete) => void
}

// memo()d so add-form keystrokes in the parent don't re-reconcile the roster
// (WoO / HLR run ~4k athletes). The inline row-edit state lives HERE, not in
// the parent, for the same reason — edit-input keystrokes stay inside this
// subtree. The memo holds because `groups` only changes identity when the
// athletes array actually changes (parent useMemo), and onSave/onDelete are
// useCallback-stable.
const AthleteGroupList = memo(function AthleteGroupList({ groups, canEdit, busy, single, onSave, onDelete, onToggleActive }: GroupListProps) {
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editBirthdate, setEditBirthdate] = useState('')
  const [editActive, setEditActive] = useState(false)

  function startEdit(a: SportAthlete) {
    setEditingId(a.id)
    setEditName(a.name)
    // <input type="date"> only accepts a bare YYYY-MM-DD value.
    setEditBirthdate((a.birthdate ?? '').slice(0, 10))
    setEditActive(a.is_active)
  }

  async function saveEdit(athleteId: number) {
    const name = editName.trim()
    if (!name) return
    const ok = await onSave(athleteId, { name, birthdate: editBirthdate || null, is_active: editActive })
    if (ok) setEditingId(null)
  }

  async function confirmDelete(a: SportAthlete) {
    // Leave edit mode only on an actual delete — a cancelled confirm keeps
    // the row editable. (A stale editingId would otherwise disable every ⋯
    // button, since only one row may edit at a time.)
    const ok = await onDelete(a)
    if (ok) setEditingId(null)
  }

  function renderGroup(g: Group) {
    if (g.hideWhenEmpty && g.rows.length === 0) return null
    return (
      <div key={g.label} className="ad-athlete-group">
        {/* Group header + column headers sit OUTSIDE the scroll box, so
            they stay fixed while long rosters scroll underneath. */}
        <div className="ad-athlete-group-head">
          <span className="b-mono">{g.label}</span>
          <span className="ad-athlete-count b-mono">{g.rows.length}</span>
        </div>
        {g.rows.length === 0
          ? <p className="b-mono-dim ad-list-empty">none</p>
          : (
              <>
                <div className="ad-athlete-cols-head b-mono-dim" aria-hidden="true">
                  <span>Name</span>
                  <span>Date of Birth</span>
                  <span>Age</span>
                  <span>Active</span>
                </div>
                <div className="ad-athlete-scroll">
                  {g.rows.map(a => (
                    editingId === a.id
                      ? (
                          <div key={a.id} className="ad-athlete-row is-editing">
                            <input
                              type="text"
                              className="ad-input"
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                              placeholder="Athlete name…"
                            />
                            <input
                              type="date"
                              className="ad-input"
                              value={editBirthdate}
                              onChange={e => setEditBirthdate(e.target.value)}
                              aria-label="Birthdate"
                            />
                            <label className="ad-athlete-edit-active b-mono-dim b-tiny">
                              <input
                                type="checkbox"
                                checked={editActive}
                                onChange={e => setEditActive(e.target.checked)}
                              />
                              ACTIVE
                            </label>
                            <div className="ad-athlete-actions">
                              <button
                                type="button"
                                className="ad-list-remove"
                                onClick={() => confirmDelete(a)}
                                disabled={busy}
                                aria-label={`Delete ${a.name}`}
                              >
                                ✕
                              </button>
                              <button
                                type="button"
                                className="b-btn-ghost b-btn-tiny"
                                onClick={() => setEditingId(null)}
                                disabled={busy}
                              >
                                CANCEL
                              </button>
                              <button
                                type="button"
                                className="b-btn b-btn-primary b-btn-tiny"
                                onClick={() => saveEdit(a.id)}
                                disabled={busy || !editName.trim()}
                              >
                                {busy ? 'SAVING…' : 'SAVE'}
                              </button>
                            </div>
                          </div>
                        )
                      : (
                          <div key={a.id} className={`ad-athlete-row${g.minor ? ' is-minor' : ''}`}>
                            <span className="ad-athlete-name">{a.name}</span>
                            <span className="b-mono-dim b-tiny">{a.birthdate ? a.birthdate.slice(0, 10) : '—'}</span>
                            <span className="b-mono-dim b-tiny">{a.age != null ? `${a.age}y` : '—'}</span>
                            {canEdit
                              ? (
                                  <button
                                    type="button"
                                    className={`ad-athlete-active${a.is_active ? ' is-on' : ''}`}
                                    onClick={() => onToggleActive(a)}
                                    disabled={busy || editingId !== null}
                                    aria-pressed={a.is_active}
                                    aria-label={`Toggle active for ${a.name}`}
                                    title={a.is_active ? 'On the active list — click to remove' : 'Click to mark active'}
                                  >
                                    {a.is_active ? '●' : '○'}
                                  </button>
                                )
                              : (
                                  <span
                                    className={`ad-athlete-active${a.is_active ? ' is-on' : ''}`}
                                    title={a.is_active ? 'On the active list' : undefined}
                                  >
                                    {a.is_active ? '●' : '○'}
                                  </span>
                                )}
                            {canEdit && (
                              <div className="ad-athlete-actions">
                                <button
                                  type="button"
                                  className="ad-athlete-more"
                                  onClick={() => startEdit(a)}
                                  disabled={busy || editingId !== null}
                                  aria-label={`Edit ${a.name}`}
                                >
                                  ⋯
                                </button>
                              </div>
                            )}
                          </div>
                        )
                  ))}
                </div>
              </>
            )}
      </div>
    )
  }

  // Search results are one full-width list; misses read better without
  // empty sibling columns.
  if (single) {
    return (
      <div className="ad-athletes-columns is-single">
        <div className="ad-athletes-col">
          {groups.map(renderGroup)}
        </div>
      </div>
    )
  }

  // Layout-only three-column split — the memo props are untouched, so the
  // 4k-row render isolation holds.
  return (
    <div className="ad-athletes-columns">
      <div className="ad-athletes-col ad-athletes-col--left">
        {groups.filter(g => g.column === 'left').map(renderGroup)}
      </div>
      <div className="ad-athletes-col ad-athletes-col--right">
        {groups.filter(g => g.column === 'right').map(renderGroup)}
      </div>
    </div>
  )
})

// ATHLETES — per-sport roster grouped by age bracket. Rows are directly
// editable (inline edit / delete per row, add form at the bottom) rather
// than the section-level EDIT/SAVE mode, since each mutation is its own
// API call.
export default function AthletesPanel({ sportId, canEdit }: Props) {
  const [athletes, setAthletes] = useState<SportAthlete[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  // Add form.
  const [addName, setAddName] = useState('')
  const [addBirthdate, setAddBirthdate] = useState('')
  const [addActive, setAddActive] = useState(false)

  // Multi-athlete lookup — traders paste several names at once (comma or
  // newline separated) and read the ages off the result list. Debounced so
  // keystrokes don't re-filter the 4k-row rosters on every character.
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    let cancelled = false
    setAthletes(null)
    setMsg(null)
    setAddName('')
    setAddBirthdate('')
    setAddActive(false)
    setQuery('')
    setDebouncedQuery('')
    setActiveOnly(false)
    fetchSportAthletes(sportId)
      .then((rows) => {
        if (!cancelled) setAthletes(rows)
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
    return () => {
      cancelled = true
    }
  }, [sportId])

  // Distinct lowercased search terms. Newlines survive textarea paste, so a
  // column copied straight out of a spreadsheet works as-is.
  const terms = useMemo(() => {
    const seen = new Set<string>()
    for (const raw of debouncedQuery.split(/[,;\n]+/)) {
      const t = raw.trim().toLowerCase()
      if (t) seen.add(t)
    }
    return [...seen]
  }, [debouncedQuery])

  const { groups, searching, misses, activeCount } = useMemo(() => {
    const all: AthleteRow[] = (athletes ?? []).map((a) => {
      const rawAge = a.birthdate ? computeAge(a.birthdate) : null
      // A future birthdate (typo) computes negative — treat it as unknown
      // rather than letting it land in UNDER 18.
      const age = rawAge != null && rawAge >= 0 ? rawAge : null
      return { ...a, age }
    })
    const activeCount = all.reduce((n, a) => n + (a.is_active ? 1 : 0), 0)
    const pool = activeOnly ? all.filter(a => a.is_active) : all
    const byName = (a: AthleteRow, b: AthleteRow) => a.name.localeCompare(b.name)

    // Search mode: one flat result list, plus the terms that matched nobody
    // — a trader pasting six names needs to see the misses, not infer them.
    if (terms.length > 0) {
      const seen = new Set<number>()
      const matched: AthleteRow[] = []
      const misses: string[] = []
      for (const t of terms) {
        let hit = false
        for (const a of pool) {
          if (a.name.toLowerCase().includes(t)) {
            hit = true
            if (!seen.has(a.id)) {
              seen.add(a.id)
              matched.push(a)
            }
          }
        }
        if (!hit) misses.push(t)
      }
      matched.sort(byName)
      const groups: Group[] = [
        { label: 'SEARCH RESULTS', rows: matched, column: 'left' as const },
      ]
      return { groups, searching: true, misses, activeCount }
    }

    const u18: AthleteRow[] = []
    const u21: AthleteRow[] = []
    const adult: AthleteRow[] = []
    const unknown: AthleteRow[] = []
    for (const row of pool) {
      if (row.age == null) unknown.push(row)
      else if (row.age < 18) u18.push(row)
      else if (row.age < 21) u21.push(row)
      else adult.push(row)
    }
    for (const list of [u18, u21, adult, unknown]) list.sort(byName)
    const groups: Group[] = [
      // Array order drives the right column's stack: U21, then U18, then
      // UNKNOWN at the bottom.
      { label: 'UNDER 21', rows: u21, column: 'right' as const },
      { label: 'UNDER 18', rows: u18, minor: true, column: 'right' as const },
      { label: '21+', rows: adult, column: 'left' as const },
      { label: 'UNKNOWN', rows: unknown, hideWhenEmpty: true, column: 'right' as const },
    ]
    return { groups, searching: false, misses: [] as string[], activeCount }
  }, [athletes, terms, activeOnly])

  // Stable identities so AthleteGroupList's memo() holds across add-form
  // keystrokes and msg changes.
  const saveAthlete = useCallback(async (athleteId: number, payload: AthleteWritePayload): Promise<boolean> => {
    setBusy(true)
    setMsg(null)
    try {
      await updateSportAthlete(sportId, athleteId, payload)
      setAthletes(await fetchSportAthletes(sportId))
      setMsg({ kind: 'ok', text: `Saved ${payload.name}.` })
      return true
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
      return false
    }
    finally {
      setBusy(false)
    }
  }, [sportId])

  const removeAthlete = useCallback(async (a: SportAthlete): Promise<boolean> => {
    // window.confirm matches the delete-container precedent in OffersPanel.
    if (!window.confirm(`Delete athlete "${a.name}"? This is permanent.`)) return false
    setBusy(true)
    setMsg(null)
    try {
      await deleteSportAthlete(sportId, a.id)
      setAthletes(await fetchSportAthletes(sportId))
      setMsg({ kind: 'ok', text: `Deleted ${a.name}.` })
      return true
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
      return false
    }
    finally {
      setBusy(false)
    }
  }, [sportId])

  // One-click roster flag from the row's ●/○ — resends name/birthdate
  // unchanged so the upsert RPC only sees the flag flip.
  const toggleActive = useCallback(async (a: SportAthlete): Promise<void> => {
    setBusy(true)
    setMsg(null)
    try {
      await updateSportAthlete(sportId, a.id, {
        name: a.name,
        birthdate: a.birthdate ? a.birthdate.slice(0, 10) : null,
        is_active: !a.is_active,
      })
      setAthletes(await fetchSportAthletes(sportId))
      setMsg({ kind: 'ok', text: a.is_active ? `${a.name} removed from the active list.` : `${a.name} marked active.` })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }, [sportId])

  async function add() {
    const name = addName.trim()
    if (!name) return
    setBusy(true)
    setMsg(null)
    try {
      await createSportAthlete(sportId, { name, birthdate: addBirthdate || null, is_active: addActive })
      setAthletes(await fetchSportAthletes(sportId))
      setAddName('')
      setAddBirthdate('')
      setAddActive(false)
      setMsg({ kind: 'ok', text: `Added ${name}.` })
    }
    catch (e) {
      // Includes the inline 409 duplicate-name message from the fetcher.
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <div className="ad-editor-section">
      <div className="ad-section-head">
        <h3 className="b-mono">ATHLETES</h3>
        <button
          type="button"
          className={`b-btn b-btn-tiny${activeOnly ? ' b-btn-primary' : ''}`}
          onClick={() => setActiveOnly(v => !v)}
          aria-pressed={activeOnly}
          title="Show only athletes flagged active"
        >
          ACTIVE ONLY
          {athletes ? ` (${activeCount})` : ''}
        </button>
      </div>

      <textarea
        className="ad-input ad-athlete-search"
        value={query}
        onChange={e => setQuery(e.target.value)}
        rows={1}
        placeholder="Search athletes — separate multiple names with commas or new lines…"
        aria-label="Search athletes"
      />
      {searching && misses.length > 0 && (
        <p className="b-mono-dim b-tiny ad-athlete-misses">
          NO MATCH: {misses.join(', ')}
        </p>
      )}

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}

      {!athletes
        ? <p className="b-mono-dim b-tiny">Loading…</p>
        : (
            <div className="ad-athletes">
              <AthleteGroupList
                groups={groups}
                canEdit={canEdit}
                busy={busy}
                single={searching}
                onSave={saveAthlete}
                onDelete={removeAthlete}
                onToggleActive={toggleActive}
              />

              {canEdit && (
                <div className="ad-athlete-add">
                  <input
                    type="text"
                    className="ad-input"
                    value={addName}
                    onChange={e => setAddName(e.target.value)}
                    placeholder="Athlete name…"
                  />
                  <input
                    type="date"
                    className="ad-input"
                    value={addBirthdate}
                    onChange={e => setAddBirthdate(e.target.value)}
                    aria-label="Birthdate (optional)"
                  />
                  <label className="ad-athlete-edit-active b-mono-dim b-tiny">
                    <input
                      type="checkbox"
                      checked={addActive}
                      onChange={e => setAddActive(e.target.checked)}
                    />
                    ACTIVE
                  </label>
                  <button
                    type="button"
                    className="b-btn b-btn-tiny"
                    onClick={add}
                    disabled={busy || !addName.trim()}
                  >
                    ADD ATHLETE
                  </button>
                </div>
              )}
            </div>
          )}
    </div>
  )
}
